// ///////////////////////////////////////////////////////////////////////////////// //
// -----------------------  Rainfall Accumulation Tracker ------------------------   //
// --------------------- The code to generate the mapbox map ----------------------  //
//                                                                                   //
//  There are several url parameters available to customise it:                      //
//   - region: selects the regions data to show.                                     //
//       Defaults to Europe, other options available are:                            //
//       | europe, north_america, south_america, africa,                             //
//       | asia, india, indian_ocean, australia                                      //
//   - lat and lon: Sets the center of the map, defaults to the center of region     //
//   - zoom: Sets the zoom level, range:[1 - 20] - defaults to 3                     //
//   - date: Selects a specific date. Format [yyyy_mm_dd]  - defaults to yesterday   //
//                                                                                   //
// ///////////////////////////////////////////////////////////////////////////////// //


import { Child } from 'pym.js';
// import ai2html from './ai2html-graphic.html';
import * as d3 from 'd3';
import meta from './data/meta.json';

import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/ /;

const mapboxgl = require('mapbox-gl');

const pkg = require('../package.json');

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

console.clear();

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
stylePremium(CNNPolitics, CNNBusiness);

// regions with all their centers and a label
const regions = {
  europe: { bounds: [[-30, 60], [50, 30]], centroid: [10, 45], label: 'Europa' },
  india: { bounds: [[60, 40], [100, 0]], centroid: [80, 20], label: 'India' },
  indian_ocean: { bounds: [[40, 10], [115, -60]], centroid: [77.5, -25], label: 'El océano Índico' },
  middle_east: { bounds: [[20, 60], [90, 10]], centroid: [55, 35], label: 'Medio Oriente' },
  north_america: { bounds: [[-179.95555, 60], [-30, 20]], centroid: [-96, 39], label: 'Norte América' },
  south_america: { bounds: [[-100, 60], [-30, -70]], centroid: [-58, -13], label: 'Sudamérica' },
  australia: { bounds: [[100, 10], [179.95555, -60]], centroid: [134, -25], label: 'Australia' },
  asia: { bounds: [[90, 10], [179.95555, 60]], centroid: [134.977775, 35], label: 'Asia' },
  africa: { bounds: [[-20, 65], [50, -65]], centroid: [10, -10], label: 'África' },
};

// get the url parameters
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

// create variables with them all, or their defaults
const region = urlParams.has('region') ? urlParams.get('region') : 'europe';
const lat = urlParams.has('lat') ? urlParams.get('lat') : 0;
const lon = urlParams.has('lon') ? urlParams.get('lon') : 0;
const default_center = [lon, lat];
const label = urlParams.has('label') ? urlParams.get('label') : regions[region].label;

// build the URL for the file we want
const base_url = urlParams.has('date') ? 'https://ix.cnn.io/data/rainfall-tracker-europe/archive' : 'https://ix.cnn.io/data/rainfall-tracker-europe';
const date = urlParams.has('date') ? `_${urlParams.get('date')}` : '';
const url = `${base_url}/rainfall_accumulation_${region}_1day${date}.json`;

const container = d3.select('#graphic');
container.html('');

container.append('div')
  .attr('id', 'map')
  .attr('style', 'width: 100%; height: 600px;');

mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

// Load data before beginning to set up the map so it can download, don't think this really makes a different
d3.json(url).then((data) => {
  // Update hed, dek and timestamp
  const { last_updated } = data[0];

  // Formatea la fecha en español
  const fecha = new Date(last_updated);
  const fechaFormateada = new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(fecha);

  // Actualiza los textos con la fecha en español
  d3.select('#hed').text(`Acumulación de lluvia en ${label}`);
  d3.select('#dek').text(`${fechaFormateada}`);
  d3.select('#timestamp').text(`Datos al ${fechaFormateada}`);

  // subset of just the geojson data
  data = data[0].data;

  // set map params
  const map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/cnndigital/cm2yzafi700tb01o063rvaunz',
    zoom: urlParams.has('zoom') ? urlParams.get('zoom') : 3.3,
    maxZoom: 18,
    minZoom: 1,
    center: urlParams.has('lat') ? default_center : regions[region].centroid,
    maxBounds: null,
    // padding: {
    //   left: 300, right: 100, top: 100, bottom: 30,
    // },
    bearing: 0,
    scrollZoom: false,
    dragRotate: false,
    logoPosition: 'bottom-left',
    touchPitch: false,
    interactive: true,
  });

  function defineMaxBounds() {
    console.log(map.getZoom());
    if (map.getZoom() < 3.6) {
      map.setMaxBounds(regions[region].bounds);
    } else {
      map.setMaxBounds(null);
    }
  }

  defineMaxBounds();

  map.on('zoomend', () => {
    defineMaxBounds();
  });

  const nav = new mapboxgl.NavigationControl({
    showCompass: false,
  });
  map.addControl(nav, 'top-right');

  // hide random little mapbox button that does nothing
  d3.selectAll('.mapboxgl-ctrl-attrib-button')
    .style('display', 'none');

  map.on('load', () => {
    map.addSource('polygons', {
      type: 'geojson',
      data,
    });

    // Add a layer to style polygons by 'precip' value
    map.addLayer({
      id: 'polygons-layer',
      type: 'fill',
      source: 'polygons',
      paint: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'precip'],
          1, '#fff',
          10, '#87d1fc',
          20, '#006098',
          150, '#00324f',
        ],
        'fill-opacity': 0.4,
      },
    },
    'water');

    pymChild.sendHeight();
  });
});
